import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, PauseCircle, PlayCircle, Users, ShieldCheck, BadgeDollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { fmtKES as fmtKes } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Jargon } from "@/components/admin/JargonTerm";

/**
 * SuperAdminAgenciesCard — read + write card for the Admin dashboard.
 *
 * Lists every agency on the platform with:
 *   - Seat usage bar
 *   - Subscription status pill
 *   - Suspend / Activate button
 *   - Seat-limit edit (inline)
 *
 * Data source: /api/admin/agencies (list) + per-agency mutations.
 */

import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

export default function SuperAdminAgenciesCard() {
    const [agencies, setAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    // Activate-annual modal state
    const [activateFor, setActivateFor] = useState(null);   // agency doc
    const [amountKes, setAmountKes] = useState("");
    const [paymentRef, setPaymentRef] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const { sorted: sortedAgencies, sortKey, direction, requestSort } = useSortableData(agencies, {
        accessors: {
            agency_name: (a) => a.agency_name || "",
            cma:         (a) => a.cma_license_no || "",
            seats:       (a) => Number(a.seats_used || 0),
            plan:        (a) => a.plan_type || "",
            status:      (a) => a.subscription_status || (a.active ? "active" : "inactive"),
            mrr:         (a) => Number(a.monthly_kes || 0),
        },
    });

    const reload = async () => {
        try {
            const r = await api.get("/admin/agencies");
            setAgencies(r.data.agencies || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load agencies");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const toggle = async (a) => {
        const path = a.active ? "suspend" : "activate";
        try {
            await api.post(`/admin/agencies/${a.id}/${path}`);
            toast.success(`Agency ${path}d`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    const patchSeats = async (a) => {
        const val = window.prompt(`New seat limit for ${a.agency_name} (current: ${a.seat_limit}, used: ${a.seats_used})`, a.seat_limit);
        if (!val) return;
        const n = parseInt(val, 10);
        if (isNaN(n) || n < 1) { toast.error("Enter a positive integer"); return; }
        try {
            await api.patch(`/admin/agencies/${a.id}/seats`, { seat_limit: n });
            toast.success(`Seat limit updated to ${n}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    // 7-day trial → annual lifecycle actions -----------------------------------
    const verifyLicense = async (a) => {
        if (!window.confirm(
            `Start the 7-day free trial for "${a.agency_name}"?\n\n` +
            `You've reviewed their uploaded ${a.institution_type === "moe" ? "MoE certificate" : "CMA license"} ` +
            `and it looks legit. All institutional features will unlock immediately for 7 days.`
        )) return;
        try {
            await api.post(`/admin/agencies/${a.id}/verify-license`, { note: "" });
            toast.success(`Trial started for ${a.agency_name}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verification failed");
        }
    };

    const openActivateAnnual = (a) => {
        setActivateFor(a);
        setAmountKes((a.institution_type === "moe") ? "350000" : "500000");
        setPaymentRef("");
        setNote("");
    };

    const submitActivateAnnual = async () => {
        if (!activateFor) return;
        const amt = parseInt(amountKes, 10);
        if (isNaN(amt) || amt < 1000) return toast.error("Enter the amount received (KES)");
        if (paymentRef.trim().length < 2) return toast.error("Enter the bank reference");
        setSaving(true);
        try {
            await api.post(`/admin/agencies/${activateFor.id}/activate-annual`, {
                amount_kes: amt,
                payment_reference: paymentRef.trim(),
                note: note.trim() || null,
            });
            toast.success(`Annual plan activated for ${activateFor.agency_name}`);
            setActivateFor(null);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Activation failed");
        } finally {
            setSaving(false);
        }
    };

    // Small helpers for the plan pill / row highlighting ------------------------
    const planPill = (a) => {
        const plan = a.plan_type || "pending";
        const cls = {
            pending: "bg-muted text-muted-foreground",
            trial:   "bg-primary/15 text-primary",
            annual:  "bg-emerald-500/15 text-emerald-500",
            monthly: "bg-accent/15 text-accent-foreground",
            expired: "bg-red-500/15 text-red-500",
        }[plan] || "bg-muted text-muted-foreground";
        return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`}>{plan.toUpperCase()}</span>;
    };

    return (
        <div className="border border-border bg-card rounded p-4" data-testid="admin-agencies-card">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    <div className="overline">Agencies · {agencies.length}</div>
                </div>
                <button onClick={reload} className="text-xs text-muted-foreground hover:text-foreground"
                        data-testid="admin-agencies-refresh">
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : agencies.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                    No agencies provisioned yet.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40 text-xs uppercase tracking-widest text-muted-foreground">
                            <tr>
                                <SortableTh label="Agency" sortKey="agency_name" currentKey={sortKey} direction={direction} onSort={requestSort} />
                                <SortableTh label="CMA #"  sortKey="cma"         currentKey={sortKey} direction={direction} onSort={requestSort} />
                                <SortableTh label="Seats"  sortKey="seats"       currentKey={sortKey} direction={direction} onSort={requestSort} />
                                <SortableTh label="Plan"   sortKey="plan"        currentKey={sortKey} direction={direction} onSort={requestSort} />
                                <SortableTh label="Status" sortKey="status"     currentKey={sortKey} direction={direction} onSort={requestSort} />
                                <SortableTh label="MRR"    sortKey="mrr"        currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                                <th className="text-right px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="admin-agencies-tbody">
                            {sortedAgencies.map((a) => {
                                const usage = (a.seats_used || 0) / Math.max(1, a.seat_limit || 100);
                                return (
                                    <tr key={a.id} className="border-t border-border transition-colors duration-150 hover:bg-muted/40"
                                        data-testid={`admin-agency-row-${a.id}`}>
                                        <td className="px-3 py-2">{a.agency_name}</td>
                                        <td className="px-3 py-2 mono text-xs text-muted-foreground">
                                            {a.cma_license_no}
                                        </td>
                                        <td className="px-3 py-2 w-56">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs">
                                                    {a.seats_used || 0} / {a.seat_limit || 100}
                                                </span>
                                                <div className="flex-1 h-1.5 rounded bg-secondary overflow-hidden">
                                                    <div
                                                        className={"h-full " + (
                                                            usage > 0.9 ? "bg-danger"
                                                            : usage > 0.7 ? "bg-primary"
                                                            : "bg-accent"
                                                        )}
                                                        style={{ width: `${Math.min(100, usage * 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            {planPill(a)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold " + (
                                                a.active
                                                    ? "bg-success/10 text-success"
                                                    : "bg-danger/10 text-danger"
                                            )}>
                                                {a.subscription_status?.toUpperCase() || "ACTIVE"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right mono">{fmtKes(a.monthly_kes)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="inline-flex flex-wrap gap-2 justify-end">
                                                {a.plan_type === "pending" && (
                                                    <button
                                                        onClick={() => verifyLicense(a)}
                                                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                                                        data-testid={`admin-agency-verify-${a.id}`}
                                                        title="Verify license & start 7-day trial"
                                                    >
                                                        <ShieldCheck className="w-3.5 h-3.5" />
                                                        Start trial
                                                    </button>
                                                )}
                                                {(a.plan_type === "trial" || a.plan_type === "expired" || a.plan_type === "monthly") && (
                                                    <button
                                                        onClick={() => openActivateAnnual(a)}
                                                        className="text-xs text-emerald-500 hover:underline inline-flex items-center gap-1"
                                                        data-testid={`admin-agency-activate-annual-${a.id}`}
                                                        title="Confirm annual payment received"
                                                    >
                                                        <BadgeDollarSign className="w-3.5 h-3.5" />
                                                        Activate annual
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => patchSeats(a)}
                                                    className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                                                    data-testid={`admin-agency-seats-${a.id}`}
                                                    title="Change seat limit"
                                                >
                                                    <Users className="w-3.5 h-3.5" />
                                                    Seats
                                                </button>
                                                <button
                                                    onClick={() => toggle(a)}
                                                    className={
                                                        "text-xs inline-flex items-center gap-1 " + (
                                                            a.active
                                                                ? "text-danger hover:underline"
                                                                : "text-success hover:underline"
                                                        )
                                                    }
                                                    data-testid={`admin-agency-toggle-${a.id}`}
                                                >
                                                    {a.active
                                                        ? <><PauseCircle className="w-3.5 h-3.5" />Suspend</>
                                                        : <><PlayCircle className="w-3.5 h-3.5" />Activate</>
                                                    }
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Activate-Annual modal — super admin confirms bank wire received */}
            <Dialog open={!!activateFor} onOpenChange={(open) => !open && setActivateFor(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Activate annual plan · {activateFor?.agency_name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2 text-sm">
                        <div className="text-xs text-muted-foreground">
                            Expected: <b className="text-foreground">KES {(activateFor?.institution_type === "moe" ? 350000 : 500000).toLocaleString()}</b>
                            {" "}({activateFor?.institution_type === "moe" ? "MoE" : "CMA"} annual plan). Adjust below if partial or negotiated.
                        </div>
                        <div>
                            <Label htmlFor="annual-amount">Amount received (KES)</Label>
                            <Input
                                id="annual-amount"
                                type="number"
                                value={amountKes}
                                onChange={(e) => setAmountKes(e.target.value)}
                                data-testid="annual-amount-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="annual-ref">Bank transfer reference</Label>
                            <Input
                                id="annual-ref"
                                value={paymentRef}
                                onChange={(e) => setPaymentRef(e.target.value)}
                                placeholder="e.g. FT23FEB1234"
                                data-testid="annual-ref-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="annual-note">Internal note (optional)</Label>
                            <Input
                                id="annual-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="e.g. partial payment, invoice #123"
                                data-testid="annual-note-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActivateFor(null)} disabled={saving}>Cancel</Button>
                        <Button onClick={submitActivateAnnual} disabled={saving} data-testid="annual-activate-submit">
                            {saving ? "Activating…" : "Activate 12-month plan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
